<script setup lang="ts">
import { Ref, onBeforeMount } from "vue";
import { userUserStore } from "../../../stores/user";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import router from "../../../router";
import { getAuth } from "@firebase/auth";
import { ref } from "vue";
import LoadingIcon from "../../../base-components/LoadingIcon";
import { FormInput } from "../../../base-components/Form";
import { asignarCustomClaim } from "../../../services/cloudFunctions/asignarCustomClaim";
import { signOut } from "firebase/auth";
import {
  getDownloadURL,
  getStorage,
  ref as refStorage
} from "firebase/storage";
import { invitacionAceptar } from "../../../services/cloudFunctions/invitacionAceptar";
import Button from "../../../base-components/Button";
import { usuarioConverter, UsuarioEmpresas } from "@fixeduy/restaurantestypes";

const userStore = userUserStore();
const db = getFirestore();
const loading = ref(true);
const loadingMensaje = ref("Cargando datos de usuario...");
const buscarEmpresa = ref("");

const handlerCerrarSesion = () => {
  const auth = getAuth();
  signOut(auth)
    .then(() => {
      userStore.signOut();
    })
    .catch(error => {
      // An error happened.
    });
};

const empresaFiltro = (empresa: UsuarioEmpresas) => {
  const buscar = buscarEmpresa.value.toLocaleLowerCase();
  return (
    buscar == "" ||
    empresa.nombreComercial.toLocaleLowerCase().search(buscar) != -1 ||
    empresa.razonSocial.toLocaleLowerCase().search(buscar) != -1 ||
    empresa.rut.toLocaleLowerCase().search(buscar) != -1
  );
};

// const handlerSelecEmpresa = async (empresa: UsuarioEmpresas) => {
//   loading.value = true;
//   loadingMensaje.value = `Accediendo a ${empresa.nombreComercial}`;
//   // assign custom claim
//   asignarCustomClaim({
//     empresaSolicitada: empresa.id
//   }).then(result => {
//     const auth = getAuth();
//     if (result.exito) {
//       // load token
//       auth.currentUser!.getIdTokenResult(true).then(async idTokenResult => {
//         userStore.setUserToken(idTokenResult);
//         await userStore.setEmpresasActual(empresa);
//         router.replace("/");
//       });
//     } else {
//       handlerCerrarSesion();
//     }
//   });
//   await new Promise(resolve => setTimeout(resolve, 1500));
//   localStorage.setItem("recargar_app", Date.now().toString());
// };

const handlerSelecEmpresa = async (empresa: UsuarioEmpresas) => {
  try {
    loading.value = true;
    loadingMensaje.value = `Accediendo a ${empresa.nombreComercial}`;

    const auth = getAuth();

    const result = await asignarCustomClaim({
      empresaSolicitada: empresa.id
    });

    if (!result?.exito) {
      await handlerCerrarSesion();
      return;
    }

    if (!auth.currentUser) {
      await handlerCerrarSesion();
      return;
    }

    const idTokenResult = await auth.currentUser.getIdTokenResult(true);
    userStore.setUserToken(idTokenResult);

    await userStore.setEmpresasActual(empresa);

    localStorage.setItem("recargar_app", Date.now().toString());

    await router.replace("/");
  } catch (error) {
    console.error("Error seleccionando empresa:", error);
    await handlerCerrarSesion();
  } finally {
    loading.value = false;
  }
};

const logosURLs: Ref<{ [key: string]: string }> = ref({});

onBeforeMount(async () => {
  await new Promise(resolve => setTimeout(resolve, 1500));
  if (userStore.userLoggedIn != null) {
    const invitacionesEmpresas = await userStore.cargarInvitaciones();
    if (invitacionesEmpresas.length > 0) {
      for (let i = 0; i < invitacionesEmpresas.length; i++) {
        const invitacion = invitacionesEmpresas[i];
        loadingMensaje.value = `Aceptando invitación a ${invitacion.empresaNombreComercial}...`;
        await invitacionAceptar({
          idEmpresa: invitacion.empresaId
        });
      }
    }
    getDoc(
      doc(db, `usuarios/${userStore.userLoggedIn.uid}`).withConverter(
        usuarioConverter
      )
    )
      .then(docUser => {
        if (docUser.exists()) {
          const usuario = docUser.data();
          userStore.setEmpresasRegistradas(usuario.empresas);
          userStore.setOpciones(
            usuario.googleApi,
            usuario.googleApiEmpresasHabilitadas
          );
          // if (usuario.empresas.length > 1) {
          loading.value = false;
          // }
          if (usuario.empresas.length == 1) {
            handlerSelecEmpresa(usuario.empresas[0]);
          }
        } else {
          // El usuario no existe, no tiene empresas asignadas
          loading.value = false;
        }
      })
      .catch(() => {
        userStore.signOut();
        signOut(getAuth());
      });
  } else {
    handlerCerrarSesion();
  }

  if (userStore.empresasRegistradas.length == 1) {
    // assign custom claim
    asignarCustomClaim({
      empresaSolicitada: userStore.empresasRegistradas[0].id
    })
      .then(result => {
        // load token
        if (result) {
          const auth = getAuth();
          if (auth.currentUser != null) {
            auth.currentUser
              .getIdTokenResult(true)
              .then(async idTokenResult => {
                userStore.setUserToken(idTokenResult);
                await userStore.setEmpresasActual(
                  userStore.empresasRegistradas[0]
                );
                router.replace("/");
              })
              .catch(() => {
                userStore.signOut();
                signOut(getAuth());
              });
          } else {
            userStore.signOut();
          }
        }
      })
      .catch(() => {
        userStore.signOut();
        signOut(getAuth());
      });
  }

  const storage = getStorage();
  userStore.empresasRegistradas.forEach(async empresa => {
    if (empresa.logoURL != null && empresa.logoURL != "") {
      const url = await getDownloadURL(refStorage(storage, empresa.logoURL));
      logosURLs.value[empresa.id] = url;
    }
  });
});

const cancelarLogin = () => {
  userStore.signOut();
  signOut(getAuth());
};

const ambiente = import.meta.env.VITE_ENV_NOMBRE;
const irProduccion = () => {
  window.location.href = "https://acceso.atiendo.cloud";
};
</script>

<template>
  <div v-if="loading">
    <div
      class="flex flex-col items-center justify-end col-span-6 sm:col-span-3 xl:col-span-2"
    >
      <div class="w-64">
        <div class="flex flex-col items-center">
          <div class="w-8">
            <LoadingIcon icon="oval" class="w-8 h-8" />
          </div>
        </div>
        <p class="mt-5 text-center">{{ loadingMensaje }}</p>
        <div class="flex justify-center mt-5">
          <div class="flex-1">
            <Button
              variant="secondary"
              size="sm"
              class="mb-2 mr-1"
              @click="cancelarLogin"
            >
              Cerrar sesión
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else>
    <div v-if="userStore.empresasRegistradas.length > 0">
      <template v-if="ambiente != 'Produccion'">
        <p class="mt-5 text-danger p-3 bg-white">
          Estas en el sistema de prueba/Demo
        </p>
        <Button variant="primary" class="mb-2 mr-1" @click="irProduccion">
          Click aquí para ir a produccion
        </Button>
      </template>
      <div class="col-span-12 mt-3 md:col-span-6 xl:col-span-4 2xl:col-span-12">
        <div class="flex items-center h-10 intro-x">
          <h2 class="mr-5 text-lg font-medium truncate">
            Selecciona qué empresa quieres usar
          </h2>
        </div>
        <div v-if="userStore.empresasRegistradas.length >= 4">
          <FormInput
            type="text"
            v-model="buscarEmpresa"
            placeholder="Buscar empresa"
          />
        </div>
        <div
          class="mt-5 relative before:block before:absolute before:w-px before:h-[85%] before:bg-slate-200 before:dark:bg-darkmode-400 before:ml-5 before:mt-5"
        >
          <template
            v-for="empresa in userStore.empresasRegistradas"
            :key="empresa.id"
          >
            <div
              v-if="empresaFiltro(empresa)"
              class="relative flex items-center mb-3 intro-x"
              @click="handlerSelecEmpresa(empresa)"
            >
              <div class="flex-1 py-3 ml-4 box zoom-in">
                <div class="flex">
                  <template v-if="logosURLs[empresa.id]">
                    <div class="flex-1 max-w-[160px] p-4">
                      <img
                        :src="logosURLs[empresa.id]"
                        style="
                          max-width: 144px;
                          max-height: 84px;
                          margin-bottom: 10px;
                        "
                      />
                    </div>
                    <div class="flex-1 p-4">
                      <div class="font-medium">
                        {{ empresa.nombreComercial }}
                      </div>
                      <div class="mt-1 text-slate-900 dark:text-slate-100">
                        {{ empresa.razonSocial }}
                      </div>
                      <div class="mt-1 text-slate-900 dark:text-slate-100">
                        {{ empresa.rut }}
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <div class="flex-1 p-4">
                      <div class="font-medium">
                        {{ empresa.nombreComercial }}
                      </div>
                      <div class="mt-1 text-slate-900 dark:text-slate-100">
                        {{ empresa.razonSocial }}
                      </div>
                      <div class="mt-1 text-slate-900 dark:text-slate-100">
                        {{ empresa.rut }}
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    <div v-else class="text-left">
      <p class="font-medium">Sin empresas</p>
      <p class="mt-3 mb-5">
        No tienes empresas registradas en Atiendo para tu usuario con:
        {{ userStore.user?.email }}
      </p>
      <template v-if="ambiente != 'Produccion'">
        <p class="mt-5 text-danger p-3 bg-white">
          Estas en el sistema de prueba/Demo
        </p>
        <Button variant="primary" class="mb-2 mr-1" @click="irProduccion">
          Click aquí para ir a produccion
        </Button>
      </template>
      <p>
        <router-link to="/registro">
          <Button variant="primary" class="w-32 mb-2 mr-1">
            Registrarme
          </Button></router-link
        >
      </p>
      <p>
        <Button
          variant="outline-danger"
          size="sm"
          class="w-32 mb-2 mr-1 mt-3"
          @click="handlerCerrarSesion"
        >
          Cerrar sesión
        </Button>
      </p>
    </div>
  </div>
</template>
